import * as THREE from 'three';
import { ViewerObservation } from '../ViewerElement/ObservationElement';
import { ViewerElementHandler } from '../ViewerElement/ViewerElementHandler';
import { Walkthrough } from '../ViewerElement/WalkthroughElement';
import { ThreeSixtyViewer } from '../ThreeSixtyViewer';
import { ViewerMode, ViewerScreenDirection } from '../Types';
import { Tooltip } from '../ViewerElement/ElementTooltip/ElementTooltip';

export class InteractionHandler {
  // Core Three.js objects and interaction state
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private hoveredObservation: ViewerObservation | null = null;
  private hoveredWalkthrough: Walkthrough | null = null;
  private readonly hoverDistanceThreshold = 35; // pixels

  // Handlers for observations and walkthroughs
  private _observationHandler!: ViewerElementHandler;
  private _walkthroughHandler!: ViewerElementHandler;

  // Stores the screen direction of this viewer instance (for split/half screen modes)
  private screenDirection: ViewerScreenDirection;

  // Static tooltip instance for all InteractionHandler instances
  private static tooltip = new Tooltip();

  constructor(
    private scene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    screenDirection: ViewerScreenDirection,
  ) {
    this.screenDirection = screenDirection;
    // You may attach event listeners here (e.g., canvas.addEventListener('mousemove', this.onMouseMove.bind(this)))
  }

  // Setter injection for handlers
  public set observationHandler(handler: ViewerElementHandler) {
    this._observationHandler = handler;
  }
  public set walkthroughHandler(handler: ViewerElementHandler) {
    this._walkthroughHandler = handler;
  }

  // ======================================================
  // Public Event Handlers
  // ======================================================

  /**
   * Handle mouse move events by updating the hovered object and tooltips.
   * @param event - The mouse event.
   */
  public onMouseMove(event: MouseEvent): void {
    this.updateMousePosition(event);
    // Only update the hovered object if one is not already active
    if (!this.hoveredObservation && !this.hoveredWalkthrough) {
      this.updateHoveredObject();
    }
    // Update tooltips if hovering over an observation or a walkthrough.
    if (this.hoveredObservation) {
      this.handleObservationTooltip(event);
    } else if (this.hoveredWalkthrough) {
      this.handleWalkthroughTooltip(event);
    }
  }

  /**
   * Handle mouse down events (clicks) to trigger observation or walkthrough actions.
   * @param event - The mouse event.
   */
  public onMouseDown(event: MouseEvent): void {
    if (!this._observationHandler) return;
    this.updateMousePosition(event);
    const intersections = this.getIntersections();

    if (intersections.length > 0) {
      const intersectedObject = intersections[0].object;
      const observation = this._observationHandler.findElement(intersectedObject);
      if (
        observation &&
        this._observationHandler.onElementClick &&
        !this._observationHandler.elementsDisabled
      ) {
        this._observationHandler.onElementClick(observation.id);
        InteractionHandler.tooltip.hide();
        this.hoveredObservation = null;
        return;
      }

      const walkthrough = this._walkthroughHandler?.findElement(intersectedObject);
      if (walkthrough && this._walkthroughHandler?.onElementClick) {
        InteractionHandler.tooltip.hide();
        this.hoveredWalkthrough = null;
        this._walkthroughHandler.onElementClick(walkthrough.nextPointId);
      }
    }
  }

  // ======================================================
  // Private Helper Methods
  // ======================================================

  /**
   * Update the currently hovered object based on a raycast into the scene.
   */
  private updateHoveredObject(): void {
    const intersections = this.getIntersections();
    if (intersections.length === 0) {
      return;
    }

    const intersectedObject = intersections[0].object;
    this.hoveredObservation =
      (this._observationHandler?.findElement(intersectedObject) as ViewerObservation) || null;

    // If no observation was found, try to find a walkthrough object.
    if (!this.hoveredObservation) {
      this.hoveredWalkthrough =
        (this._walkthroughHandler?.findElement(intersectedObject) as Walkthrough) || null;
    }
  }

  /**
   * Handle displaying or hiding the observation tooltip based on the mouse distance.
   * @param event - The mouse event.
   */
  private handleObservationTooltip(event: MouseEvent): void {
    if (!this.hoveredObservation) return;

    const { element3dObject, observationData } = this.hoveredObservation;
    const screenPosition = this.toScreenPosition(element3dObject.position.clone(), this.camera);
    const distance = this.calculateDistance(
      event.clientX,
      event.clientY,
      screenPosition.x,
      screenPosition.y,
    );

    if (distance < this.getHoverDistanceThreshold() && ThreeSixtyViewer.isObservationVisible) {
      InteractionHandler.tooltip.show(
        Tooltip.createObservationContent(observationData),
        event.clientX,
        event.clientY,
      );
    } else {
      InteractionHandler.tooltip.hide();
      this.hoveredObservation = null;
    }
  }

  private getHoverDistanceThreshold(): number {
    return 4000 / this.camera.fov - 14.07;
  }

  /**
   * Handle displaying or hiding the walkthrough tooltip based on the mouse distance.
   * @param event - The mouse event.
   */
  private handleWalkthroughTooltip(event: MouseEvent): void {
    if (!this.hoveredWalkthrough) return;

    const { element3dObject, name } = this.hoveredWalkthrough;
    const screenPosition = this.toScreenPosition(element3dObject.position.clone(), this.camera);
    const distance = this.calculateDistance(
      event.clientX,
      event.clientY,
      screenPosition.x,
      screenPosition.y,
    );

    if (distance < this.hoverDistanceThreshold) {
      InteractionHandler.tooltip.show(
        Tooltip.createTitleContent(name),
        event.clientX,
        event.clientY,
      );
    } else {
      InteractionHandler.tooltip.hide();
      this.hoveredWalkthrough = null;
    }
  }

  /**
   * Calculate the Euclidean distance between two points.
   * @param x1 - X-coordinate of point 1.
   * @param y1 - Y-coordinate of point 1.
   * @param x2 - X-coordinate of point 2.
   * @param y2 - Y-coordinate of point 2.
   * @returns The distance between the two points.
   */
  private calculateDistance(x1: number, y1: number, x2: number, y2: number): number {
    const dx = x1 - x2;
    const dy = y1 - y2;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Convert a 3D position to 2D screen coordinates, accounting for different viewer modes.
   * @param position - The 3D position.
   * @param camera - The camera used for projection.
   * @returns The corresponding 2D screen position.
   */
  private toScreenPosition(
    position: THREE.Vector3,
    camera: THREE.PerspectiveCamera,
  ): { x: number; y: number } {
    const vector = position.project(camera);
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    switch (ThreeSixtyViewer.viewerMode) {
      case ViewerMode.SPLIT_SCREEN:
        if (this.screenDirection === ViewerScreenDirection.LEFT) {
          return {
            x: (vector.x + 1) * (screenWidth / 4),
            y: (-vector.y + 1) * (screenHeight / 2),
          };
        } else if (this.screenDirection === ViewerScreenDirection.RIGHT) {
          return {
            x: (vector.x + 1) * (screenWidth / 4) + screenWidth / 2,
            y: (-vector.y + 1) * (screenHeight / 2),
          };
        }
        break;
      case ViewerMode.HALF_SCREEN:
        return {
          x: (vector.x + 1) * (screenWidth / 4) + screenWidth / 2,
          y: (-vector.y + 1) * (screenHeight / 2),
        };
      default:
        break;
    }

    return {
      x: (vector.x + 1) * (screenWidth / 2),
      y: (-vector.y + 1) * (screenHeight / 2),
    };
  }

  /**
   * Update the normalized mouse coordinates based on the current mouse event and viewer mode.
   * @param event - The mouse event.
   * @returns The determined viewer screen direction.
   */
  private updateMousePosition(event: MouseEvent): ViewerScreenDirection {
    const { clientX, clientY } = event;
    const { innerWidth, innerHeight } = window;

    if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
      if (clientX < innerWidth / 2) {
        this.mouse.x = (clientX / (innerWidth / 2)) * 2 - 1;
        this.mouse.y = -(clientY / innerHeight) * 2 + 1;
        return ViewerScreenDirection.LEFT;
      } else {
        this.mouse.x = ((clientX - innerWidth / 2) / (innerWidth / 2)) * 2 - 1;
        this.mouse.y = -(clientY / innerHeight) * 2 + 1;
        return ViewerScreenDirection.RIGHT;
      }
    } else if (ThreeSixtyViewer.viewerMode === ViewerMode.HALF_SCREEN) {
      this.mouse.x = ((clientX - innerWidth / 2) / (innerWidth / 2)) * 2 - 1;
      this.mouse.y = -(clientY / innerHeight) * 2 + 1;
      return ViewerScreenDirection.RIGHT;
    } else {
      this.mouse.x = (clientX / innerWidth) * 2 - 1;
      this.mouse.y = -(clientY / innerHeight) * 2 + 1;
      return ViewerScreenDirection.RIGHT;
    }
  }

  /**
   * Perform a raycast using the current mouse position and return any intersections.
   * @returns An array of raycaster intersections.
   */
  private getIntersections(): THREE.Intersection[] {
    this.raycaster.setFromCamera(this.mouse, this.camera);
    return this.raycaster.intersectObjects(this.scene.children, true);
  }
}
